import type { CellContext, Row } from '@tanstack/react-table';
import { DateTime } from 'luxon';
import { type DataRequestV2, RequestStatus } from '~/common/types';

export type DateKey = keyof Pick<
  DataRequestV2,
  | 'createdAt'
  | 'firstContactedAt'
  | 'secondContactedAt'
  | 'thirdContactedAt'
  | 'fourthContactedAt'
  | 'resolutionDate'
  | 'nextFollowUpAt'
>;

export function isPastDue(request: DataRequestV2): boolean {
  if (
    request.requestStatus === RequestStatus.Complete ||
    request.requestStatus === RequestStatus.Cancelled ||
    request.requestStatus === RequestStatus.Dismissed
  )
    return false;
  if (request['nextFollowUpAt']) {
    const now = DateTime.now().startOf('day');
    const nextFollowUp = request['nextFollowUpAt'].startOf('day');
    const diff = now.diff(nextFollowUp, 'days').days;
    return diff >= 0;
  }
  return false;
}

export function nextFollowUpSortingFn(rowA: Row<DataRequestV2>, rowB: Row<DataRequestV2>): number {
  const a = rowA.original;
  const b = rowB.original;
  const key = 'nextFollowUpAt';

  const pA = isPastDue(a);
  const pB = isPastDue(b);

  // if both are past due, sort by most overdue
  if (pA && pB && a[key] && b[key]) {
    const now = DateTime.now().startOf('day');
    const dA = now.diff(a[key].startOf('day'), 'days').days;
    const dB = now.diff(b[key].startOf('day'), 'days').days;
    return dA > dB ? -1 : 1;
  }

  // if first one is passed and not second, push the second down
  if (pA && !pB) return -1;
  // opposite of above
  if (!pA && pB) return 1;

  // if neither exists, sort by current cell value
  if (!pA && !pB) {
    return (a[key]?.startOf('day')?.toString() || '').localeCompare(b[key]?.startOf('day')?.toString() || '');
  }

  // else, keep sorting the same
  return 1;
}

export function mdy(d?: DateTime) {
  return d?.toFormat('M/d/yyyy') || '';
}

export async function searchRelatedEmail(requestId: string) {
  const dataResearchInboxBase = window.localStorage.getItem('dataResearchInboxBase');

  if (dataResearchInboxBase) {
    window.open(`${dataResearchInboxBase}/#search/${requestId}`, '_blank');
  } else {
    alert(
      'Needs manual configuration, see https://www.notion.so/civilgrid/Setting-up-your-Email-in-CivilSearch-27e81357ce69805ba223da26d1370946',
    );
  }
}

export const NextFollowUpCell = ({ cell }: { cell: CellContext<DataRequestV2, unknown> }) => {
  const request = cell.row.original;

  const v = cell.getValue<string>();

  // allow follow up if past due
  if (isPastDue(request)) {
    return (
      <div
        className="h-full w-full cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          searchRelatedEmail(request.requestId);
        }}
      >
        {v}
      </div>
    );
  }

  return <>{v}</>;
};
